import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { catchError, of } from 'rxjs';
import { BriefResponseDto, BriefService, VideoFormatEnum } from '@/app/services/brief/brief.service';
import { ImageService } from '@/app/services/image/image.service';
import { LoadingInlineComponent } from '@/app/atoms/loading-inline/loading-inline.component';
import {
  briefTypeFromBrief,
  briefVisibilityLabel,
  formatBriefTrackerDate,
} from '@/app/pages/admin-options/settings/molecules/brief-tracker/brief-tracker.helpers';
import { summariseBriefAsk } from '@/app/common/brief-ask/summarise-brief-ask';

@Component({
  selector: 'admin-brief-details',
  imports: [LoadingInlineComponent],
  templateUrl: './brief-details.html',
  host: { class: 'block h-full w-full min-h-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BriefDetails implements OnInit {
  briefId = input<string | null>(null);

  readonly brief = signal<BriefResponseDto | undefined>(undefined);
  readonly loadingState = signal<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  readonly briefIdCopied = signal(false);

  readonly copyIdButtonClass = computed(() =>
    this.briefIdCopied()
      ? 'border-green-500/45 text-green-500'
      : 'border-zinc-400/35 dark:border-zinc-500/45',
  );

  readonly copyIdAriaLabel = computed(() =>
    this.briefIdCopied() ? 'Copied brief ID' : 'Copy brief ID',
  );

  readonly imageService = inject(ImageService);
  private readonly sanitizer = inject(DomSanitizer);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private briefService = inject(BriefService);

  ngOnInit(): void {
    const fromInput = this.briefId();
    const fromRoute = this.route.snapshot.paramMap.get('briefId');
    const id = fromInput ?? fromRoute;
    if (id) {
      this.loadBrief(id);
    }
  }

  briefTypeLabel(b: BriefResponseDto): string {
    return briefTypeFromBrief(b).label;
  }

  briefTypeDetail(b: BriefResponseDto): string | null {
    return briefTypeFromBrief(b).detail;
  }

  briefAsk(b: BriefResponseDto) {
    return summariseBriefAsk(b);
  }

  visibilityLabel(b: BriefResponseDto): string {
    return briefVisibilityLabel(b);
  }

  formatDate(value: Date | string | undefined): string {
    if (!value) {
      return '—';
    }
    return formatBriefTrackerDate(value);
  }

  hasKeyMessage(b: BriefResponseDto): boolean {
    return !!b.stepsHtml?.trim();
  }

  keyMessageHtml(b: BriefResponseDto): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(b.stepsHtml?.trim() || '');
  }

  videoDurationLabel(b: BriefResponseDto): string {
    const price = b.currentPrice;
    if (!price) {
      return '—';
    }
    if (price.fifteenSecPrice > 0 && price.thirtySecPrice === 0 && price.sixtySecPrice === 0) {
      return 'Under 15s';
    }
    if (price.fifteenSecPrice === 0 && price.thirtySecPrice > 0 && price.sixtySecPrice === 0) {
      return '15s to 30s';
    }
    if (price.fifteenSecPrice === 0 && price.thirtySecPrice === 0 && price.sixtySecPrice > 0) {
      return '30s to 60s';
    }
    return '—';
  }

  videoDurationIcon(b: BriefResponseDto): string | null {
    const price = b.currentPrice;
    if (!price) {
      return null;
    }
    if (price.fifteenSecPrice > 0 && price.thirtySecPrice === 0 && price.sixtySecPrice === 0) {
      return 'assets/icon-brief/15secs.svg';
    }
    if (price.fifteenSecPrice === 0 && price.thirtySecPrice > 0 && price.sixtySecPrice === 0) {
      return 'assets/icon-brief/30secs.svg';
    }
    if (price.fifteenSecPrice === 0 && price.thirtySecPrice === 0 && price.sixtySecPrice > 0) {
      return 'assets/icon-brief/60secs.svg';
    }
    return null;
  }

  videoAspectRatioLabel(b: BriefResponseDto): string {
    switch (b.videoFormat) {
      case VideoFormatEnum.Vertical:
        return '9:16 · Vertical';
      case VideoFormatEnum.Horizontal:
        return '16:9 · Horizontal';
      case VideoFormatEnum.Square:
        return '1:1 · Square';
      default:
        return '—';
    }
  }

  videoAspectRatioIcon(b: BriefResponseDto): string | null {
    switch (b.videoFormat) {
      case VideoFormatEnum.Vertical:
        return 'assets/icon/vertical_format.svg';
      case VideoFormatEnum.Horizontal:
        return 'assets/icon/horizontal_format.svg';
      case VideoFormatEnum.Square:
        return 'assets/icon/square_format.svg';
      default:
        return null;
    }
  }

  noodleEnabledLabel(b: BriefResponseDto): string {
    return b.enableNoodle ? 'Yes' : 'No';
  }

  copyBriefId(id: string): void {
    void navigator.clipboard.writeText(id).then(() => {
      this.briefIdCopied.set(true);
      window.setTimeout(() => this.briefIdCopied.set(false), 2000);
    });
  }

  private loadBrief(id: string): void {
    this.loadingState.set('loading');
    this.briefService
      .getBriefById(id)
      .pipe(
        catchError(() => {
          this.loadingState.set('error');
          return of(undefined);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((brief) => {
        if (brief) {
          this.brief.set(brief);
          this.loadingState.set('loaded');
        }
      });
  }
}
